/**
 * Auto break-even: the shared setting and what the engine has done with it.
 *
 * Auto B/E used to run in every open tab (PositionControlsOverlay), each with
 * its own on/off state, and an idle phone tab moved a live stop on its own.
 * It now runs once, in utt-engine (utt-engine/src/auto_be). This store only
 * MIRRORS it:
 *
 * - `enabled` is the one shared setting (Redis `auto-be:settings`, default
 *   OFF). The toggle writes it optimistically and the `auto-be:settings`
 *   broadcast reconciles every device.
 * - `fired` / `failed` are per position (broker:account:contract), filled from
 *   the engine's `auto-be:fired` / `auto-be:failed` broadcasts and cleared by
 *   `auto-be:reset` when the position goes flat.
 *
 * Nothing here ever sends an order.
 */
import { create } from 'zustand';
import { wsService } from '../services/WebSocketService';
import { getApiBaseUrl } from '../config/ports';
import { useToastStore } from './toastStore';

export const AUTO_BE_EVENTS = ['auto-be:settings', 'auto-be:fired', 'auto-be:failed', 'auto-be:reset'] as const;

export interface AutoBePositionEvent {
  brokerId: string;
  accountId: number;
  contractId: number;
  symbol?: string;
  newSl?: number;
  moved?: number;
  error?: string;
  at?: number;
}

interface AutoBeSettingsPayload {
  enabled: boolean;
  triggerPct?: number;
  executionEnabled?: boolean;
}

interface AutoBeStatePayload extends AutoBeSettingsPayload {
  positions?: Array<AutoBePositionEvent & { phase: 'inFlight' | 'done' | 'failed' | 'suppressed' }>;
}

export const autoBeKey = (brokerId: string, accountId: number, contractId: number): string =>
  `${brokerId}:${accountId}:${contractId}`;

interface AutoBeState {
  enabled: boolean;
  triggerPct: number;
  loaded: boolean;
  /** Positions whose stop the engine moved (or found already past entry). */
  fired: Record<string, AutoBePositionEvent>;
  /** Positions whose move was refused; never retried by the engine. */
  failed: Record<string, AutoBePositionEvent>;
  /** Toggle: optimistic local update, then persist; reverts on failure. */
  setEnabled: (enabled: boolean) => Promise<void>;
  applySettings: (s: AutoBeSettingsPayload) => void;
  applyState: (s: AutoBeStatePayload) => void;
  onFired: (e: AutoBePositionEvent) => void;
  onFailed: (e: AutoBePositionEvent) => void;
  onReset: (e: AutoBePositionEvent) => void;
}

const keyOf = (e: AutoBePositionEvent) => autoBeKey(e.brokerId, e.accountId, e.contractId);

const without = (rec: Record<string, AutoBePositionEvent>, key: string) => {
  if (!(key in rec)) return rec;
  const next = { ...rec };
  delete next[key];
  return next;
};

export const useAutoBeStore = create<AutoBeState>((set, get) => ({
  enabled: false,
  triggerPct: 80,
  loaded: false,
  fired: {},
  failed: {},

  setEnabled: async (enabled) => {
    const previous = get().enabled;
    set({ enabled });
    try {
      const res = await fetch(`${getApiBaseUrl()}/auto-be`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      get().applyState(await res.json());
    } catch (err) {
      set({ enabled: previous });
      useToastStore.getState().addToast({
        type: 'error',
        message: `Auto B/E setting not saved: ${err instanceof Error ? err.message : String(err)}`,
      });
    }
  },

  applySettings: (s) => {
    if (typeof s?.enabled !== 'boolean') return;
    set((st) => ({
      enabled: s.enabled,
      triggerPct: typeof s.triggerPct === 'number' ? s.triggerPct : st.triggerPct,
      loaded: true,
      // Turning it off re-arms every position on the engine; mirror that.
      ...(s.enabled ? {} : { fired: {}, failed: {} }),
    }));
  },

  applyState: (s) => {
    get().applySettings(s);
    if (!Array.isArray(s?.positions)) return;
    const fired: Record<string, AutoBePositionEvent> = {};
    const failed: Record<string, AutoBePositionEvent> = {};
    for (const p of s.positions) {
      if (p.phase === 'done') fired[keyOf(p)] = p;
      else if (p.phase === 'failed') failed[keyOf(p)] = p;
    }
    set({ fired, failed });
  },

  onFired: (e) => {
    const key = keyOf(e);
    if (get().fired[key]) return; // shown once
    set((st) => ({ fired: { ...st.fired, [key]: e }, failed: without(st.failed, key) }));
    if ((e.moved ?? 0) > 0 && typeof e.newSl === 'number') {
      useToastStore.getState().addToast({
        type: 'success',
        message: `Auto B/E: SL moved to ${e.newSl.toFixed(2)} (${get().triggerPct}% of TP reached)`,
      });
    }
  },

  onFailed: (e) => {
    const key = keyOf(e);
    if (get().failed[key]) return;
    set((st) => ({ failed: { ...st.failed, [key]: e } }));
    useToastStore.getState().addToast({
      type: 'error',
      message: `Auto B/E could not move the SL: ${e.error ?? 'refused'}`,
      duration: 8000,
    });
  },

  onReset: (e) => {
    const key = keyOf(e);
    set((st) => ({ fired: without(st.fired, key), failed: without(st.failed, key) }));
  },
}));

/** Pull the engine's current view (on connect and reconnect). */
export async function fetchAutoBeState(): Promise<void> {
  try {
    const res = await fetch(`${getApiBaseUrl()}/auto-be`);
    if (!res.ok) return;
    useAutoBeStore.getState().applyState(await res.json());
  } catch {
    // Engine unreachable: keep what we have; the next connect retries.
  }
}

let handlersInitialized = false;

export function initAutoBeSocketHandlers(): void {
  void fetchAutoBeState();
  if (handlersInitialized) return;
  handlersInitialized = true;
  const store = () => useAutoBeStore.getState();
  wsService.on('auto-be:settings', (s: AutoBeSettingsPayload) => store().applySettings(s));
  wsService.on('auto-be:fired', (e: AutoBePositionEvent) => store().onFired(e));
  wsService.on('auto-be:failed', (e: AutoBePositionEvent) => store().onFailed(e));
  wsService.on('auto-be:reset', (e: AutoBePositionEvent) => store().onReset(e));
}

export function resetAutoBeSocketHandlers(): void {
  handlersInitialized = false;
  for (const event of AUTO_BE_EVENTS) wsService.off(event);
}

export type AutoBeBadgeStatus = 'armed' | 'done' | 'failed' | null;

/**
 * What to show on a position. The engine acts only on the selected account's
 * positions that have both a TP and an SL, and never while hedge mode is on,
 * so "armed" is claimed only under those same conditions.
 */
export function autoBeStatusFor(args: {
  enabled: boolean;
  fired: Record<string, AutoBePositionEvent>;
  failed: Record<string, AutoBePositionEvent>;
  brokerId: string;
  accountId: number;
  contractId: number;
  inScope: boolean;
  hasTp: boolean;
  hasSl: boolean;
  hedgeActive: boolean;
}): AutoBeBadgeStatus {
  if (!args.enabled || !args.inScope) return null;
  const key = autoBeKey(args.brokerId, args.accountId, args.contractId);
  if (args.fired[key]) return 'done';
  if (args.failed[key]) return 'failed';
  if (args.hedgeActive || !args.hasTp || !args.hasSl) return null;
  return 'armed';
}
